import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
// import "./ReturningCustomersLayout.css"
import * as translator from "../../../utils/translator";
import { trackContinueToShipping } from "../../../utils/otcPopupExperiment";
import ProductSelection from './components/ProductSelection'
import * as http from "../../../../src/utils/http";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import HeaderController from '../../../components/Headers/HeaderController';
import { CatalogSettingContext, RouterCartContext } from '../ChooseTreatmentRouter';
import LoadingModal from '../../../components/Modals/LoadingModal/LoadingModal';
import ServerError from '../../../components/ServerError/ServerError';
import { submitOverrideContext } from "../../../../src/components/Modals/MyAccountChangeSubscriptionMedicationModal/MyAccountChangeSubscriptionMedicationModal";
import ProductAvailabilityTag from './components/ProductAvailabilityTag';
import Disclaimer from '../components/Disclaimers/Disclaimer';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import StickyNotification from '../../../components/StickyNotification/StickyNotification';
import { loadCustomerFromSession } from '../../../customHooks/useCustomer';
import { trackOncePerSession } from '../../../PixelTrackers/trackingUtils';
import { LabelData } from '../FreeDosageChooseTreatmentV2/FreeDosageLayoutV2';
import TreatmentSelection from './components/TreatmentSelection';
import ProductSearch from './components/ProductSearch';
import { SEARCH_EVENT_DEBOUNCE_MS, MAX_SEARCH_EVENT_COUNT } from '../../../utils/consts';
import { isPriceComponentIncluded } from '../../../utils/helpers';
import { matchesPreferredType } from './dosageForm';

export interface SortingMetadata {
    abTestGroup: "A" | "B";
    sortingRule: "product_page" | "purchase_history" | "fallback";
    categoryCatalogID: number;
}


const COUNTRIES_ALLOWING_PO_DESIGN = ['FR', 'CH'];


export interface ProductItem {
    product_item_id: number,
    quantity: number,
    price: number,
    in_stock: boolean,
    custom_quantity_title: string,
    available_stocks?: {stockCode: string, officialPrice: number}[],
    express_stock?: boolean,
    official_price: number,
    service_fee: number,
    treatment_fee: number,
    shipping_fee: number
}
  

export interface ProductAdditionalData {
    overrided_product_type?: string | null,
    [key: string]: any,
}

export interface Product {
    product_id: number,
    treatment_fee: number,
    subscription_ftd: number,
    catalog_id: number
    dosage_str: string,
    title: string,
    product_title: string,
    additional_data?: string | ProductAdditionalData,
    product_settings?: { [key: string]: Array<string> }
    in_stock: boolean,
    items: ProductItem[],
    is_default_dosage: boolean,
    default_quantity?: string
    treatments_order: number,
    use_custom_quantity: boolean,
    labelData: LabelData | null,
    quantity_type: string,
    is_pre_orderable?: boolean,
}

export interface groupedProductsType {
    [key: number]: Product[]
}
interface ReturingCustomersLayoutContextType {
    currencySign: string,
    hidePrices: boolean,
    countryID: string,
    languageID: string,
    isNoPreferenceSelected: boolean,
    setIsNoPreferenceSelected: React.Dispatch<React.SetStateAction<boolean>>,
    selectedCatalogID: number | null,
    setSelectedCatalogID: React.Dispatch<React.SetStateAction<number | null>>,
    selectedProductID: number | null,
    setSelectedProductID: React.Dispatch<React.SetStateAction<number | null>>,
    groupedProducts: null | groupedProductsType,
    selectedProduct: null | Product,
    selectedProductItemID: number | null,
    setSelectedProductItemID: React.Dispatch<React.SetStateAction<number | null>>,
    setIsExpressProductItem: React.Dispatch<React.SetStateAction<boolean>>,
    productItemToPlan: Record<number, number[]>,
    subscriptionPlansData: Record<number, any>,
    selectedSubscriptionPlanID: number | null,
    setSelectedSubscriptionPlanID: React.Dispatch<React.SetStateAction<number | null>>,
    allowPrescriptionOnlyFlow: boolean,
    findByDefaultDosage: (productList: Product[]) => Product | undefined,
    findQuantityBasedOnRule: (product: Product) => ProductItem | undefined,
    onSubmit: () => any,
    defaultCatalogID: string | null,
    preferredType: string | null,
    /** Filter key/value from URL for sorting (e.g., treatment_type=stop_hair_loss, symptom=hot_flush) */
    additionalDataFilter: { key: string; value: string } | null,
    initialProduct: { catalog_id: number, product_id: number, product_item_id: number } | null,
    localeID: string,
    setIsSubmitButtonDisabled: (isDisabled: boolean) => void,
    isSubmitButtonDisabled: boolean,
    setCountryID: (countryID: string) => void,
    isExpressProductItem: boolean,
    stockCodesInRange: string[] | null,
    sortingMetadata: SortingMetadata | null,
    searchTerm: string,
    // Null = no catalog override; cards/packs keep official+service.
    displayedPriceComponents: Array<'official_price' | 'service_fee' | 'shipping_fee' | 'treatment_fee'> | null,
}



export const ReturingCustomersLayoutContext = createContext<ReturingCustomersLayoutContextType | null>(null);

const ReturningCustomersLayout = () => {
    const { country, language, category } = useParams() as {
        category: string;
        country: string;
        language: string;
    };
    const [searchParams] = useSearchParams();

    const [stockCodesInRange, setStockCodesInRange] = useState<string[]>([]);
    const isExpressShippingInRange = stockCodesInRange && stockCodesInRange.length > 0 && stockCodesInRange[0].length > 0 ? true : false;
    const navigate = useNavigate();
    const defaultCatalogID = searchParams.get("catalog_id");
    const preferredType = searchParams.get("quantity_type") || searchParams.get("preferred_type") || searchParams.get("filter");
    
    // Get filter for sorting by additional_data (e.g., treatment_type, symptom)
    const treatmentType = searchParams.get("treatment_type");
    const symptom = searchParams.get("symptom");
    const additionalDataFilter = treatmentType 
        ? { key: "treatment_type", value: treatmentType }
        : symptom 
            ? { key: "symptom", value: symptom }
            : null;
    

    const [selectedCatalogID, setSelectedCatalogID] = useState<number | null>(null);
    const [selectedProductID, setSelectedProductID] = useState<number | null>(null);
    const [selectedProductItemID, setSelectedProductItemID] = useState<number | null>(null);
    const [isExpressProductItem, setIsExpressProductItem] = useState<boolean>(false);
    const [groupedProducts, setGroupedProducts] = useState<groupedProductsType | null>(null);
    const [productItemToPlan, setProductItemToPlan] = useState<Record<number, number[]>>({});
    const [subscriptionPlansData, setSubscriptionPlansData] = useState<Record<number, any>>({});
    const [selectedSubscriptionPlanID, setSelectedSubscriptionPlanID] = useState<number | null>(null);
    const [initialProduct, setInitialProduct] = useState<{ catalog_id: number, product_id: number, product_item_id: number } | null>(null);
    const [isNoPreferenceSelected, setIsNoPreferenceSelected] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<any>(null);
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false)
    const [stickyNotification, setStickyNotification] = useState<{ refresh: boolean, level: string, show: boolean, title: string, content: string, autoFade: boolean }>({ show: false, title: "", content: "", level: "", refresh: false, autoFade: false })
    const [countryID, setCountryID] = useState<string>(country);
    const [sortingMetadata, setSortingMetadata] = useState<SortingMetadata | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const searchEventCount = useRef(0);

    const routerContext = useContext(CatalogSettingContext) as any;
    const cartContext = useContext(RouterCartContext) as any;

    const currencySign = countryID.toUpperCase() === 'CH' ? 'CHF' : '€';

    const hidePrices = routerContext?.forceHidePrices;
    const allowPrescriptionOnlyFlow = routerContext?.allowPrescriptionForOutOfStock || COUNTRIES_ALLOWING_PO_DESIGN.includes(countryID.toUpperCase());
    const isNoPrefEnabled = routerContext?.isNoPrefereceTreatmentEnabled;
    const localeID = `${language.toLowerCase()}-${countryID.toUpperCase()}`
    const showERezeptDisclaimer = routerContext?.showERezeptDisclaimer;

    const selectedProduct = useMemo(() => {
        let catalogToFind = selectedCatalogID;
        if (isNoPreferenceSelected) {
            catalogToFind = initialProduct?.catalog_id || null;
        }
        return (selectedProductID && groupedProducts && catalogToFind) ? groupedProducts[catalogToFind].find((product) => product.product_id === selectedProductID)! : null;
    }, [selectedProductID, groupedProducts, selectedCatalogID, isNoPreferenceSelected]);

    const findByDefaultDosage = (productList: Product[]) => {
        let defaultProduct: Product | undefined ;
        for (const product of productList as Product[]) {
            if (product.is_default_dosage && (product.in_stock || product.is_pre_orderable)) {
                defaultProduct = product;
                break;
            }
        }

        if (defaultProduct) {
            return defaultProduct;
        }

        for (const product of productList as Product[]) {
            if (product.in_stock || product.is_pre_orderable) {
                return product;
            }
        }

        return undefined;

    }

    const findQuantityBasedOnRule = (product: Product) => {
        try {

            let defaultQuantity = product.items.find((item) => item.in_stock);
            let candidate: ProductItem | undefined = undefined;
            let allowedProducts = product.items.filter((item) => item.in_stock);

            switch (product.default_quantity) {
                case "min_quantity":
                    candidate = allowedProducts.sort((a, b) => a.quantity - b.quantity)[0];
                    break;

                case "max_quantity":
                    candidate = allowedProducts.sort((a, b) => b.quantity - a.quantity)[0];
                    break;

                case "second_from_min_available":
                    candidate = allowedProducts.sort((a, b) => a.quantity - b.quantity)[1];
                    break;

                case "second_from_max_available":
                    candidate = allowedProducts.sort((a, b) => b.quantity - a.quantity)[1];
                    break;

                default:
                    candidate = allowedProducts.sort((a, b) => a.quantity - b.quantity)[0];
                    break;
            }

            if (candidate) {
                defaultQuantity = candidate;
            }

            return defaultQuantity || product.items[0];
        } catch (err: any) {
            return product.items.find((item) => item.in_stock);
        }

    }

    const selectInitialProduct = useCallback((groupedProducts: any, productItemToPlan: Record<number, number[]>) => {
        let sortedGroupedProducts = Object.values(groupedProducts).sort((a: any, b: any) => a[0].treatments_order - b[0].treatments_order);
        if (preferredType) {
            sortedGroupedProducts = [...sortedGroupedProducts].sort((a: any, b: any) => {
                const aMatch = Number(matchesPreferredType(a[0], preferredType));
                const bMatch = Number(matchesPreferredType(b[0], preferredType));
                return bMatch - aMatch;
            });
        }
        // Sort by additionalDataFilter (e.g., treatment_type, symptom) - must be last to take priority
        if (additionalDataFilter) {
            sortedGroupedProducts = [...sortedGroupedProducts].sort((a: any, b: any) => {
                const aAdditionalData = typeof a[0].additional_data === 'string' ? JSON.parse(a[0].additional_data || '{}') : (a[0].additional_data || {});
                const bAdditionalData = typeof b[0].additional_data === 'string' ? JSON.parse(b[0].additional_data || '{}') : (b[0].additional_data || {});
                const aMatch = Number(aAdditionalData[additionalDataFilter.key] === additionalDataFilter.value);
                const bMatch = Number(bAdditionalData[additionalDataFilter.key] === additionalDataFilter.value);
                return bMatch - aMatch;
            });
        }
        const cart = cartContext?.cartData;
        if (isNoPrefEnabled) {
            setIsNoPreferenceSelected(true);
        }
        //first attempt to select intial by default product settings
        let defaultProduct: Product | undefined;
        
        if (defaultCatalogID && groupedProducts[Number(defaultCatalogID)]) { //if there is a default catalog id in the url
            const prodCatalog: Product[] = groupedProducts[Number(defaultCatalogID)];

            const prod = findByDefaultDosage(prodCatalog);
            if (prod) {
                setSelectedProductID(prod.product_id);
                !isNoPrefEnabled && setSelectedCatalogID(prod.catalog_id);
                const firstQuantityInStock = findQuantityBasedOnRule(prod);
                setSelectedProductItemID(firstQuantityInStock!.product_item_id);
                setIsExpressProductItem((firstQuantityInStock?.express_stock) || false);
                const plans = productItemToPlan && productItemToPlan[firstQuantityInStock?.product_item_id || 0];
                setSelectedSubscriptionPlanID(plans ? plans[0] : 0);
                setInitialProduct({ catalog_id: prod.catalog_id, product_id: prod.product_id, product_item_id: firstQuantityInStock!.product_item_id });
                return
            }

        }

        //if there is an item from this catalog in the cart
        if (cart.product_item_id !== null){
            for (const productList of sortedGroupedProducts) {
                for (const product of productList as Product[]) {
                    const item = product.items.find((item) => item.product_item_id === cart.product_item_id)
                    if (item) {
                        if (item.in_stock || (COUNTRIES_ALLOWING_PO_DESIGN.includes(countryID.toUpperCase()) && allowPrescriptionOnlyFlow)) {
                            setSelectedProductID(product.product_id);
                            !isNoPrefEnabled && setSelectedCatalogID(product.catalog_id);
                            const firstQuantityInStock = findQuantityBasedOnRule(product);
                            setSelectedProductItemID(cart.product_item_id);
                            setIsExpressProductItem((item?.express_stock) || false);
                            const plans = productItemToPlan && productItemToPlan[firstQuantityInStock?.product_item_id || 0];
                            setSelectedSubscriptionPlanID(plans ? plans[0] : 0);
                            setInitialProduct({ catalog_id: product.catalog_id, product_id: product.product_id, product_item_id: cart.product_item_id });
                            return
                        }
                        else{
                            setStickyNotification({ show: true, title: translator.translate(language, "default", "StickyNotification/out_of_stock_title"), content: translator.translate(language, "default", "StickyNotification/out_of_stock_description"), level: "warning", refresh: false, autoFade: true });
                        }
                    }
                }
            }
        }

        for (const productList of sortedGroupedProducts) {
            defaultProduct = findByDefaultDosage(productList as Product[]);
            if (defaultProduct) {
                break;
            }
        }

        if (defaultProduct) {
            setSelectedProductID(defaultProduct.product_id);
            !isNoPrefEnabled && setSelectedCatalogID(defaultProduct.catalog_id);
            const firstQuantityInStock = findQuantityBasedOnRule(defaultProduct);
            setSelectedProductItemID(firstQuantityInStock!.product_item_id);
            setIsExpressProductItem(firstQuantityInStock?.express_stock || false);
            const plans = productItemToPlan && productItemToPlan[firstQuantityInStock?.product_item_id || 0];
            setSelectedSubscriptionPlanID(plans ? plans[0] : 0);
            setInitialProduct({ catalog_id: defaultProduct.catalog_id, product_id: defaultProduct.product_id, product_item_id: firstQuantityInStock!.product_item_id });
            return
        }


        //otherwise find first product in stock
        let found = false;
        for (const productList of sortedGroupedProducts) {
            for (const product of productList as Product[]) {
                if (product.in_stock) {
                    found = true;
                    setSelectedProductID(product.product_id);
                    !isNoPrefEnabled && setSelectedCatalogID(product.catalog_id);
                    const firstQuantityInStock = product.items.find((item) => (item.in_stock));
                    setSelectedProductItemID(firstQuantityInStock!.product_item_id);
                    setIsExpressProductItem(firstQuantityInStock?.express_stock || false);
                    const plans = productItemToPlan && productItemToPlan[firstQuantityInStock?.product_item_id || 0];
                    setSelectedSubscriptionPlanID(plans ? plans[0] : 0);
                    setInitialProduct({ catalog_id: product.catalog_id, product_id: product.product_id, product_item_id: firstQuantityInStock!.product_item_id });
                    break;
                }
            }

            if (found) {
                break;
            }
        }

        // if all products are out of stock, select the first product in stock
        const firstProduct = (sortedGroupedProducts[0] as unknown as Product[])?.[0];
        const firstQuantity = firstProduct.items[0]
        // Auto select first product only if not in group B (A or null only)
        if ( firstProduct ) {
            setSelectedProductID(firstProduct.product_id);
            !isNoPrefEnabled && setSelectedCatalogID(firstProduct.catalog_id);
            setSelectedProductItemID(firstQuantity.product_item_id);
            setIsExpressProductItem(firstQuantity.express_stock || false);
            const plans = productItemToPlan && productItemToPlan[firstQuantity.product_item_id || 0];
            setSelectedSubscriptionPlanID(plans ? plans[0] : 0);
            setInitialProduct({ catalog_id: firstProduct.catalog_id, product_id: firstProduct.product_id, product_item_id: firstQuantity.product_item_id });
        }
    }, [groupedProducts, allowPrescriptionOnlyFlow, productItemToPlan, additionalDataFilter, preferredType]);

    const { postChangeSubscriptionMediaction } = useContext(submitOverrideContext); // overrides for change subscription medication

    const handleExpressNotification = (isCustomerLoggedIn: boolean, expressProductsCounter: number) => {

        // eligible for express shipping notification
        if (expressProductsCounter > 0) {
            setStickyNotification({
                ...stickyNotification,
                show: true,
                level: "success",
                title: translator.translate(language, "default", "StickyNotification/eligible_for_express_delivery_title"),
                content: translator.translate(language, "default", "StickyNotification/eligible_for_express_delivery_description"),
                autoFade: true
            });
        }

        // ineligible for express shipping notification
        else if (stockCodesInRange && stockCodesInRange.length === 0 && !isCustomerLoggedIn) {
            setStickyNotification({
                ...stickyNotification,
                show: true,
                level: "success",
                title: translator.translate(language, "default", "StickyNotification/not_eligible_for_express_delivery_title"),
                content: translator.translate(language, "default", "StickyNotification/not_eligible_for_express_delivery_description"),
                autoFade: true
            });
        }

        

        // uber eligible without products alert
        else if (expressProductsCounter === 0 && isExpressShippingInRange) {
            window.eventsTracking.track({e:'Uber eligible without products', p:{ stock_codes: stockCodesInRange }});
        }
    }


    const onSubmit = () => {
        setIsSubmitButtonDisabled(true)
        //True:
        if (!selectedProduct || !selectedProductItemID) {
            return;
        } else if (postChangeSubscriptionMediaction) {
            postChangeSubscriptionMediaction({ product_item_id: selectedProductItemID })
            return;
        }

        const selectedItem = selectedProduct.items.find((item) => item.product_item_id === selectedProductItemID);

        MainTracker.superProperties({ "Product Name": selectedProduct?.title, "ProductItemID": selectedProductItemID });
        const productItemOutOfStock = !selectedItem?.in_stock && !selectedProduct?.is_pre_orderable;
        http.postToServer(`choose-treatment-returning-customers`, {
            selectedProductItemID,
            catalogID: selectedProduct.catalog_id,
            isExpressStock: selectedItem?.express_stock,
            prescriptionOnly: productItemOutOfStock,
            subscriptionPlanID: !productItemOutOfStock ? (selectedSubscriptionPlanID !== 0 ? selectedSubscriptionPlanID : null) : null,
            isSftd: selectedSubscriptionPlanID === 0 ? 1 : 0,
        }, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
            .then((response) => {
                let customerFromSession = loadCustomerFromSession();

                const updatedSearchParams = new URLSearchParams(searchParams); 
                
                const deeplinkCoupon = window.eventsTracking.getData("cpn")
                if (deeplinkCoupon) {
                    updatedSearchParams.set("cpn", deeplinkCoupon);
                }
                if (!customerFromSession) {
                    // if no customer id send to registration
                    navigate({ pathname: `/sign_up/${countryID}/${language}`, search: updatedSearchParams.toString() });
                } else if (cartContext?.cartData?.should_id_verify) {
                    navigate({ pathname: `/customer-verification/${countryID}/${language}`, search: updatedSearchParams.toString() });
                } else if (cartContext?.cartData?.consultation_type === "video") {
                    navigate({ pathname: `/choose-doctor/${country}/${language}/${category}`, search: updatedSearchParams.toString() });
                } else {
                    trackContinueToShipping();
                    navigate({ pathname: `/shipping/${countryID}/${language}`, search: updatedSearchParams.toString() });
                }
            })
            .catch((err) => {
                // const code = err?.response?.data?.code;
                setStickyNotification((current: any) => ({ show: true, title: "Server Failed", content: "", level: "error", refresh: !current.refresh, autoFade: current.autoFade || false }));
            })
        .finally(() =>  setIsSubmitButtonDisabled(false));

    }


    const context = {
        countryID,
        languageID: language,
        hidePrices,
        isNoPreferenceSelected,
        setIsNoPreferenceSelected,
        selectedCatalogID,
        setSelectedCatalogID,
        selectedProductID,
        setSelectedProductID,
        groupedProducts,
        selectedProduct,
        selectedProductItemID,
        setSelectedProductItemID,
        setIsExpressProductItem,
        productItemToPlan,
        subscriptionPlansData,
        selectedSubscriptionPlanID,
        setSelectedSubscriptionPlanID,
        allowPrescriptionOnlyFlow,
        findByDefaultDosage,
        findQuantityBasedOnRule,
        onSubmit,
        defaultCatalogID,
        preferredType,
        additionalDataFilter,
        initialProduct,
        localeID,
        currencySign,
        setIsSubmitButtonDisabled,
        isSubmitButtonDisabled,
        setCountryID,
        isExpressProductItem,
        stockCodesInRange,
        sortingMetadata,
        searchTerm,
        // From choose-treatment-settings; null keeps official+service on cards/packs.
        displayedPriceComponents: routerContext?.displayed_price_components ?? null,
    }

    // Debounced search tracking — same event as cannabis CT.
    useEffect(() => {
        if (!routerContext?.show_product_search) return;
        const term = searchTerm.trim();
        // Skip empty/short queries and stop after the event cap.
        if (term.length <= 2 || searchEventCount.current > MAX_SEARCH_EVENT_COUNT) return;

        const timeoutId = setTimeout(() => {
            if (!groupedProducts) return;
            // Match catalogs the same way the product list filter does.
            const words = term.split(' ').map((w) => w.toLowerCase()).filter(Boolean);
            const matchingCatalogs = Object.values(groupedProducts).filter((products) => {
                const p = products[0];
                const hay = [p?.title, p?.product_title].filter(Boolean).join(' ').toLowerCase();
                return words.every((w) => hay.includes(w));
            });
            searchEventCount.current += 1;
            MainTracker.track('custom', 'Choose Treatment: Search Performed', {
                search_term: term,
                search_event_count: searchEventCount.current,
                search_results_count: matchingCatalogs.length,
                premium_products_count: matchingCatalogs.filter((products: Product[]) => products.some((p: Product) => p.in_stock)).length,
                regular_products_count: matchingCatalogs.filter((products: Product[]) => !products.some((p: Product) => p.in_stock)).length,
                layout: 'returning_customers',
            });
        }, SEARCH_EVENT_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, groupedProducts, routerContext?.show_product_search]);

    // Handle back button navigation
    useEffect(() => {
        // Create a trap history entry so back button stays on page first
        History?.prototype?.pushState?.call(window.history, null, "", window.location.href);

        const handlePopState = (e: any) => {
            e.preventDefault();
            navigate(
                {pathname: `/questionnaire/${country}/${language}/${category}`, search: searchParams.toString()},
                { replace: true }
            );
        }

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        http.getFromServer(`choose-treatment-returning-customers/${category}/${countryID}/${language}`, [
            http.MIDDLEWARES.CUSTOMER_ID,
            http.MIDDLEWARES.CART_ORDER
        ])
            .then((response) => {
                const groupedProducts: groupedProductsType = response.data.productsGroupedByCatalog;
                
                const eligibleStockCodes = response.data.eligibleExpressStocks;
                trackOncePerSession('custom', eligibleStockCodes && eligibleStockCodes.length > 0 ? 'Uber eligable' : 'Uber not eligable', { 'Pharmacies in range': eligibleStockCodes });
                let expressProductsCounter = 0;
                if (response.data.isExpressFlow){
                    Object.values(groupedProducts).forEach((group: any) => {
                        group.forEach((product: Product) => {
                            product.items.forEach((productItem: ProductItem) => {
                                productItem.express_stock = productItem.available_stocks?.some(stock => eligibleStockCodes?.includes(stock.stockCode)) || false;
                                if (productItem.express_stock) {
                                    expressProductsCounter++;
                                }
                            });
                        });
                    });
                    setStockCodesInRange(response.data.eligibleExpressStocks);
                    handleExpressNotification(response.data.cart.customer_id !== null, expressProductsCounter);
                }

                const productItemToPlan = response.data.productItemToPlan;

                setProductItemToPlan(productItemToPlan);

                const sortingMeta = response.data.sortingMetadata;
                if (sortingMeta) {
                    setSortingMetadata(sortingMeta);
                }

                setGroupedProducts(groupedProducts);
                selectInitialProduct(groupedProducts, productItemToPlan);
                setSubscriptionPlansData(response.data.subscriptionPlansData);
                
                setLoading(false);
            })
            .catch((error) => {
                window.clientLogger.error("failed get response from server", error);
                setLoading(false);
                setError(500);
            })
    }, [])

    return (
        <>
            {loading && <LoadingModal />}

            {error && (
                <>
                    <HeaderController showSteps={false} stage={"Questionnaire"} />
                    <ServerError status={error} />
                </>
            )}
            
            <ReturingCustomersLayoutContext.Provider value={context}>
                <StickyNotification
                showNotification={stickyNotification.show}
                title={stickyNotification.title}
                onClick={() => setStickyNotification({ show: false, title: "", content: "", level: "", refresh: false, autoFade: false })}
                content={stickyNotification.content}
                level={stickyNotification.level}
                refresh={stickyNotification.refresh}
                autoFade={stickyNotification.autoFade}
                />
                {category && <HeaderController language={language} stage="Treatment" />}
                <div id='outer-container' className='bg-[#F7F7F7] min-h-screen py-1'>
                    {/* main title */}
                    <MainTitle category={category} country={countryID} language={language} displayedPriceComponents={routerContext?.displayed_price_components ?? null} />

                    {COUNTRIES_ALLOWING_PO_DESIGN.includes(countryID.toUpperCase()) && allowPrescriptionOnlyFlow ? 
                        <div id='legend-fr' className='flex flex-col md:flex-row gap-2 justify-center bg-[#F7F7F7] p-4 text-[#0D2C54] font-normal w-full'>
                        <div className='flex items-center gap-1  '>
                            <ProductAvailabilityTag inStock={true} />
                            <p>{translator.translate(language, "default", "ChooseTreatment/premium-explanation")}</p>
                        </div>

                        <div className='flex items-center gap-1 '>
                            <ProductAvailabilityTag inStock={false} />
                            <p>{translator.translate(language, "default", "ChooseTreatment/regular-explanation")}</p>
                        </div>

                    
                        </div>
                    : 
                    ""
                    }

                    {/* main content */}
                    <div id='main-content' className='flex gap-8 w-full justify-center h-full'>
                        <div id='left-panel' className='px-3 shrink-0 w-full md:w-[470px]'>
                            <div id='significance_above' className='md:pr-7 mb-5 md:hidden'>
                                {showERezeptDisclaimer && <Disclaimer text={translator.translate(language, "default", "ChooseTreatment/e-recipe-significance")} />}
                            </div>
                            {routerContext?.show_product_search ? (
                                <div id='product-search-wrap' className='mb-4 md:pr-5'>
                                    <ProductSearch value={searchTerm} onChange={setSearchTerm} />
                                </div>
                            ) : null}
                            <div id="scrollable" className='overflow-y-auto md:h-[540px]'>
                                <ProductSelection />
                            </div>
                            <div id='significance_under' className='max-md:hidden md:pr-7 mt-5 flex'>
                                {showERezeptDisclaimer && <Disclaimer text={translator.translate(language, "default", "ChooseTreatment/e-recipe-significance")} />}
                            </div>
                        </div>

                        <div id='right-panel' className='hidden md:flex max-h-full md:w-[445px] rounded-md overflow-y-auto'>
                            <TreatmentSelection />
                        </div>
                    </div>
                </div>
            </ReturingCustomersLayoutContext.Provider>
        </>
    )
}

export default ReturningCustomersLayout;


const MainTitle: React.FC<{ category: string, country: string, language: string, displayedPriceComponents: Array<'official_price' | 'service_fee' | 'shipping_fee' | 'treatment_fee'> | null }> = ({ category, country, language, displayedPriceComponents }) => {
    return <>
        <div className='flex justify-center flex-col items-center pt-10 pb-6'>
            <h1
                className="selectTreatment-title text-center flex items-center justify-center px-4 md:px-0"
            >
                {translator.translate(
                    language,
                    "default",
                    "ChooseTreatment/ChooseTreatmentTitle"
                )}
            </h1>

            {country.toUpperCase() === "FR" ? (
                <p className="selectTreatment-subtitle !my-2 w-full px-4 text-center md:px-0 md:w-[50%]">
                    {translator.translate(
                        language,
                        "default",
                        "ChooseTreatment/ChooseTreatmentSubTitle"
                    )}
                </p>
            ) : null}
        </div>
        
        {country.toUpperCase() === "DE" && (
            <div className='w-full sticky top-0 pin-below-app-bar z-[11] bg-[var(--primary-gray)] py-2 mb-0'>
                <p className='flex text-[14px] justify-center items-center text-[#0D2C54] font-poppins font-normal leading-6 opacity-40'>
                    {displayedPriceComponents && !isPriceComponentIncluded(displayedPriceComponents, 'service_fee')
                        ? translator.translate(language, 'choose_treatment', 'default/price_incl_vat_only')
                        : 'Preis inkl. MwSt. und Servicegebühr'}
                </p>
            </div>
        )}
    </>
}
